import { Component, ViewChild, OnInit, Inject } from '@angular/core';
import { Router, NavigationEnd, ActivatedRoute } from '@angular/router';
import { PusherService } from './services/pusher.service';
import { BehaviorSubject, Observable } from 'rxjs';
import { AuthService } from './services/auth.service';
import { filter } from 'rxjs/operators';
import { UserService } from './services/user.service';
import { ModalController } from '@ionic/angular';

import { DealerSelectorComponent } from './components/dealer-selector/dealer-selector.component';
import { AlertComponent } from './components/alerts/alerts.component';
import { Title } from '@angular/platform-browser';
import { ContextMenuComponent } from './components/conversations/context-menu/context-menu.component';
import { environment } from 'src/environments/environment';
import { DOCUMENT } from '@angular/common';
import { CallPopupService } from './services/call-popup.service';
import { HttpErrorResponse } from '@angular/common/http';
import { ThemeService } from './services/theme.service';
import { Capacitor } from '@capacitor/core';
import { App as CapacitorApp } from '@capacitor/app';
import { NativeNotificationService } from './services/native-notification.service';
import { PushTokenService } from './services/push-token.service';
import { VoicemailService } from './services/voicemail.service';
import { NativeTwilioVoiceService } from './services/native-twilio-voice.service';
import { ConversationService } from './services/conversation.service';
import { LeadService } from './services/leads.service';

const currentVersion = "4.0.0";

@Component({
    selector: 'app-root',
    templateUrl: 'app.component.html',
})
export class AppComponent {
    
    @ViewChild(ContextMenuComponent) contextMenu: ContextMenuComponent;
    user: any;
    isReady: boolean = false;
    uuid:any = '';
    show: any = { profile: false };
    isMenuOpen: boolean = false;
    routeName: string = '';
    SubName: string='';
    isCallPopupRoute: boolean = false;
    incomingLeadCount: number = 0;
    // private hasPrimedCallPopup = false;
    
    // Alerts
    _showAlertBanner: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    _showAlertBannerText: BehaviorSubject<string> = new BehaviorSubject<string>('');
    _showAlertUnread: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    // Alert Observables
    get showAlertBanner(): Observable<boolean> {
        return this._showAlertBanner.asObservable();
    }

    get showAlertBannerText(): Observable<string> {
        return this._showAlertBannerText.asObservable();
    }
    private isMessageBound: boolean = false;

    constructor(
        private router: Router, 
        private route: ActivatedRoute,
        private authService: AuthService,
        public userService: UserService,
        private pusher: PusherService,
        private modalCtrl: ModalController,
        private titleService: Title,
        @Inject(DOCUMENT) private document: Document,
        private callPopupService: CallPopupService,
        private themeService: ThemeService,
        private nativeNotifications: NativeNotificationService,
        private pushTokenService: PushTokenService,
        public voicemailService: VoicemailService,
        private nativeTwilioVoice: NativeTwilioVoiceService,
        private conversationService: ConversationService,
        private leadService: LeadService
    ) {
        this.themeService.initTheme();
        this.applyNativePlatformBodyClasses();
        this.registerAppUrlHandlers();
        this.nativeNotifications.init();
        this.pushTokenService.init();
        this.getAuthenticatedUser();
        this.router.events.pipe(
            filter(event => event instanceof NavigationEnd)
        ).subscribe(() => {
            this.setRouteNameAndSubName();
            this.isCallPopupRoute = this.router.url.startsWith('/call-popup');
            this.toggleCallPopupBodyClass(this.isCallPopupRoute);
        });
    }
    
    private applyNativePlatformBodyClasses(): void {
        const body = this.document?.body;
        if (!body || !Capacitor.isNativePlatform()) return;

        body.classList.add('native-app');

        if (Capacitor.getPlatform() === 'android') {
            body.classList.add('native-android');
        } else if (Capacitor.getPlatform() === 'ios') {
            body.classList.add('native-ios');
        }
    }

    ngOnInit(): void {
        this.setConversationSubscription(); // Subscribe to conversation updates
        this.nativeNotifications.requestStartupPermissions();
        this.pushTokenService.registerCurrentDevice();
    }

    private registerAppUrlHandlers(): void {
        if (!Capacitor.isNativePlatform()) {
            return;
        }

        CapacitorApp.addListener('appUrlOpen', ({ url }) => {
            this.handleNativeAppUrl(url);
        });

        CapacitorApp.getLaunchUrl()
            .then((launchData) => {
                if (launchData?.url) {
                    this.handleNativeAppUrl(launchData.url);
                }
            })
            .catch((error) => {
                console.error('Failed to read launch URL:', error);
            });
    }

    private handleNativeAppUrl(url: string): void {
        if (!url) {
            return;
        }

        try {
            const parsed = new URL(url);
            const isCallUrl =
                parsed.protocol === 'advantageconnect:' &&
                (parsed.hostname === 'call' || parsed.pathname === '/call');

            if (!isCallUrl) {
                return;
            }

            const conversationId = Number(parsed.searchParams.get('conversationId') || '');
            const action = (parsed.searchParams.get('action') || '').toLowerCase();
            const callSid = parsed.searchParams.get('callSid') || '';

            if (Number.isFinite(conversationId) && conversationId > 0) {
                this.router.navigate(['conversations'], {
                    queryParams: { id: conversationId },
                }).catch((error) => {
                    console.error('Failed to route incoming call deep link:', error);
                });
            } else if (callSid) {
                this.conversationService.getCall(callSid).subscribe({
                    next: (call) => {
                        const targetConversationId = Number(call?.conversation_id || '');
                        const extras = Number.isFinite(targetConversationId) && targetConversationId > 0
                            ? { queryParams: { id: targetConversationId } }
                            : undefined;

                        this.router.navigate(['conversations'], extras).catch((error) => {
                            console.error('Failed to route incoming call by call SID:', error);
                        });
                    },
                    error: (error) => {
                        console.error('Failed to look up incoming call conversation:', error);
                        this.router.navigate(['conversations']).catch((navError) => {
                            console.error('Failed to route incoming call fallback:', navError);
                        });
                    },
                });
            }

            if (action === 'answer') {
                if (this.nativeTwilioVoice.isNativeAndroid()) {
                    this.nativeTwilioVoice.markAnswerRequested({
                        callSid,
                        conversationId: Number.isFinite(conversationId) && conversationId > 0
                            ? conversationId
                            : null,
                    });
                } else {
                    this.callPopupService.requestAction('answer');
                }
            }
        } catch (error) {
            console.error('Failed to parse native app URL:', error);
        }
    }

    // @HostListener('document:click')
    // @HostListener('document:keydown')
    // handleUserGestureForPopup(): void {
    //     if (this.hasPrimedCallPopup) return;
    //     const opened = this.callPopupService.primePopupWindow();
    //     this.hasPrimedCallPopup = opened;
    // }

    setConversationSubscription(): void {
        this.pusher.subscribe('multi_channel_notification');

        this.pusher.channel.bind('message', (data: any) => {
            if (data.conversation) {
                const conversationData = data.conversation.data;
                if (conversationData.store === this.userService.dealer.id) {
                    this._showAlertBanner.next(true);
                    this._showAlertBannerText.next(conversationData.message || conversationData.body || '');
                    this.notifyNativeConversation(conversationData);

                    setTimeout(() => {
                        this._showAlertBanner.next(false);
                        this._showAlertBannerText.next('');
                    }, 4000);
                }
            }
            if (data.leads){
                const leadsData = data.leads.data;
                const leadsText= 'New Leads Received: ' + leadsData.total || '';
                if (leadsData.store === this.userService.dealer.id) {
                    this._showAlertBanner.next(true);
                    this._showAlertBannerText.next(leadsText);
                    this.notifyNativeleads(leadsData, leadsData.total);
                    this.refreshIncomingLeadCount();

                    
                    setTimeout(() => {
                        this._showAlertBanner.next(false);
                        this._showAlertBannerText.next('');
                    }, 4000);
            }
        }

            if (data.call) {
                const callPayload = data.call.data || data.call;
                const currentUserId = this.userService?.user?.id || this.user?.id || null;
                const currentDealerId = this.userService?.dealer?.id || this.user?.dealer?.id || null;
                const matchesAssignee = !callPayload.user_id || currentUserId === callPayload.user_id;
                const matchesDealer = !callPayload.dealer_id || currentDealerId === callPayload.dealer_id;

                if (matchesAssignee && matchesDealer && !this.nativeTwilioVoice.isNativeAndroid()) {
                    this.nativeNotifications.notifyIncomingCall(callPayload);
                }
            }

            if (data.voicemail) {
                const voicemailPayload = data.voicemail.data || data.voicemail;
                const currentUserId = this.userService?.user?.id || this.user?.id || null;
                const currentDealerId = this.userService?.dealer?.id || this.user?.dealer?.id || null;
                const isAdmin = this.userService?.admin === 1;
                const matchesAssignee = isAdmin || !voicemailPayload.user_id || currentUserId === voicemailPayload.user_id;
                const matchesDealer = !voicemailPayload.dealer_id || currentDealerId === voicemailPayload.dealer_id;

                if (matchesAssignee && matchesDealer) {
                    this.voicemailService.refreshUnreadCount();
                }
            }
        });
    }

    setRouteNameAndSubName(): void {
        let child = this.route.firstChild;
        while (child) {
            if (child.snapshot.data['title']) {
                this.routeName = child.snapshot.data['title'];
                this.SubName = child.snapshot.data['subTitle'];
                break;
            }
            child = child.firstChild;
        }
    }

    private toggleCallPopupBodyClass(enable: boolean): void {
        const body = this.document?.body;
        if (!body) return;

        if (enable) {
            body.classList.add('call-popup-body');
        } else {
            body.classList.remove('call-popup-body');
        }
    }

    getQueryParams() {
        this.uuid = this.route
    }

    private forceRelogin(): void {
        localStorage.removeItem('user');
        this.userService.user = {};
        this.user = null;
        this.isReady = true;

        if (this.router.url !== '/login') {
            this.router.navigate(['login']);
        }
    }

    async getAuthenticatedUser() {
        try {
            // console.log(environment.host);
            // console.log(environment.api_url);
            // Check if the stored version matches the current version
            const storedVersion = localStorage.getItem('app_version');
            if (storedVersion !== currentVersion) {
                localStorage.clear();
                localStorage.setItem('app_version', currentVersion);
            }
    
            // Fetch the user data from localStorage
            const rawUser = localStorage.getItem('user');
            const user = rawUser ? JSON.parse(rawUser) : null;
            const localUserToken = user?.user?.token;

            if (user && localUserToken) {
                // console.log("get authenticated user loaded");
                const response = await this.userService.getUserDetails(localUserToken).toPromise();
                const backendToken = response?.user?.token;

                // Detect localStorage token tampering/stale token and force a clean login.
                if (!backendToken || backendToken !== localUserToken) {
                    this.forceRelogin();
                    return;
                }

                localStorage.setItem('user', JSON.stringify(response));
                this.userService.setUserAccount(response);
                this.user = this.userService;
                this.isReady = true;
                this.nativeNotifications.requestStartupPermissions();
                this.pushTokenService.registerCurrentDevice();
                this.nativeTwilioVoice.syncRegistration();
                this.voicemailService.refreshUnreadCount();
                this.refreshIncomingLeadCount();
    
                // Subscribe to alerts
                this.setAlertSubscription();
    
                // Set User Alerts
                const alertCount = this.user.alertCount || 0;
                const dealerName = this.user.dealer?.name || '';
                this.titleService.setTitle(`${dealerName}${alertCount > 0 ? ` (${alertCount})` : ''} | ${this.titleService.getTitle()}`);
            } else {
                this.forceRelogin();
            }
        } catch (error) {
            console.error('Error fetching authenticated user:', error);
            const status = (error as HttpErrorResponse)?.status;
            if (status === 400 || status === 401 || status === 403) {
                this.forceRelogin();
                return;
            }
            this.isReady = true;
            this.voicemailService.refreshUnreadCount();
            this.refreshIncomingLeadCount();
        }
    }

    refreshIncomingLeadCount(): void {
        if (!this.userService?.dealer?.id) {
            this.incomingLeadCount = 0;
            return;
        }

        this.leadService.getLeads({ page: 1, sortBy: 'received_at', sortDir: 'desc' }).subscribe({
            next: (resp) => {
                this.incomingLeadCount = Number(resp['total'] || 0);
            },
            error: () => {
                this.incomingLeadCount = 0;
            },
        });
    }
    
    /**
     * [setAlertSubscription - Subscribe to pusher socket]
     * @returns {void}
     */
    setAlertSubscription(): void {
        if (this.isMessageBound) {
            // console.log("Message listener already bound, skipping...");
            return;
        }
        this.isMessageBound = true;
    
        const userChannel = this.user.admin === 1
            ? `alert.ADMIN${this.userService.dealer.id}`
            : `alert.ADV${this.user.id}`;
        
        this.pusher.subscribe('multi_channel_notification');

       this.ngOnDestroy();

    
        //console.log("Binding message listener");
        this.pusher.channel.bind('message', (data: any) => {
            const channelData = this.user.admin === 1 ? data.admin_channel : data.alert_channel;
    
            if (channelData && channelData.channel === userChannel && channelData.data.store === this.userService.dealer.id) {
                this.handleAlert(channelData.data);
            }
        });
    }
    

    ngOnDestroy(): void {
        if (this.pusher?.channel) {
            //console.log("Unbinding message listener");
            // this.pusher.channel.unbind('message');
            // this.isMessageBound = false;
        }
    }
    
    handleAlert(alertData: any): void {
        const message = typeof alertData === 'string' ? alertData : alertData?.message || '';
        this._showAlertBanner.next(true);
        this._showAlertBannerText.next(message);
        this.notifyNativeAlert(alertData);

        this.getAuthenticatedUser();
        
        setTimeout(() => {
            this._showAlertBanner.next(false);
            this._showAlertBannerText.next('');
        }, 4000);
    }

    async onDealerSelectorClick() {
        try {
            const modal = await this.modalCtrl.create({
                component: DealerSelectorComponent,
                cssClass: 'app-modal-selector',
            });
    
            await modal.present();
    
            await modal.onDidDismiss();
    
            // Restore focus after modal dismissal
            const focusableElement = document.querySelector('ion-icon[name="menu-outline"]');
            if (focusableElement instanceof HTMLElement) {
                focusableElement.focus();
            }
        } catch (error) {
            console.error('Error with Dealer Selector Modal:', error);
        }
    }
    
    async onAlertClick() {
        try {
            if (this.userService.admin !== 1) {
                await this.nativeNotifications.clearAlertNotifications();
            }

            const modal = await this.modalCtrl.create({
                component: AlertComponent,
                cssClass: 'app-modal-alerts',
            });
    
            await modal.present();
    
            await modal.onDidDismiss();
    
            // Restore focus after modal dismissal
            const focusableElement = document.querySelector('ion-icon[name="notifications-outline"]');
            if (focusableElement instanceof HTMLElement) {
                focusableElement.focus();
            }
        } catch (error) {
            console.error('Error with Alert Modal:', error);
        }
    }

    onMarkAsRead(): void {
        this.getAuthenticatedUser();
    }

    private notifyNativeAlert(alertData: any): void {
        const message = typeof alertData === 'string' ? alertData : alertData?.message || '';
        const explicitType = typeof alertData === 'string' ? '' : (alertData?.type || '').toLowerCase();
        const normalized = (message || '').toLowerCase();
        const conversationId = typeof alertData === 'string'
            ? null
            : alertData?.conversation_id || alertData?.conversationId || alertData?.ConversationId || null;
        if (!normalized || normalized.includes('incoming call')) {
            return;
        }

        if (explicitType === 'email') {
            this.nativeNotifications.notifyAlert(message, 'email', conversationId);
            return;
        }

        if (explicitType === 'sms' || explicitType === 'message') {
            this.nativeNotifications.notifyAlert(message, 'sms', conversationId);
            return;
        }

        if (normalized.includes('new email')) {
            this.nativeNotifications.notifyAlert(message, 'email', conversationId);
            return;
        }

        if (normalized.includes('new text')) {
            this.nativeNotifications.notifyAlert(message, 'sms', conversationId);
            return;
        }

        this.nativeNotifications.notifyAlert(message, 'alert');
    }

    private notifyNativeConversation(conversationData: any): void {
        const message = conversationData?.message || conversationData?.body || '';
        const type = `${conversationData?.type || conversationData?.channel_type || ''}`.toLowerCase();
        const conversationId =
            conversationData?.ConversationId ||
            conversationData?.conversationId ||
            conversationData?.conversation_id ||
            null;

        if (type.includes('email')) {
            this.nativeNotifications.notifyAlert(message || 'New email received', 'email', conversationId);
            return;
        }

        if (type.includes('sms') || type.includes('text') || type.includes('message')) {
            this.nativeNotifications.notifyAlert(message || 'New text received', 'sms', conversationId);
            return;
        }

        this.notifyNativeAlert(message);
    }

    private notifyNativeleads(leadsData: any, total: string): void {
        const type = `${leadsData?.type || leadsData?.channel_type || ''}`.toLowerCase();
        if (!type || type.includes('lead')) {
            this.nativeNotifications.notifyAlert('New leads received: ' + total, 'leads');
            return;
        }
    }
    
    

    /**
     * [onLogoutClick - Logs the current user out]
     * @returns {void}
     */
    onLogoutClick(): void {
        this.authService.logout();

        // Reset User
        this.userService.id = 0;
        this.show.profile = false;
    }

    onProfileToggle(event: Event): void {
        event.stopPropagation();
        this.show.profile = !this.show.profile;
    }

    onProfileClick(event: Event): void {
        event.stopPropagation();
    }

    onHide(): void{
        this.show.profile = false;
        this.contextMenu.deleteMessage();

    }
}
